/**
 * ProjectEditor - Timeline-based video editor for stitching shots together
 * Similar to Final Cut Pro but simplified for cartoon production
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeft, Play, Pause, SkipBack, SkipForward,
  ZoomIn, ZoomOut, Undo2, Redo2, Download, Film,
  ChevronLeft, ChevronRight, Clock, Loader2, Plus, Scissors
} from "lucide-react";

// Import modular components
import { AssetLibrary, SettingsPanel, VideoTrack, VideoAudioTrack, AudioTrack, AudioMixer, TimeRuler, Playhead, ExportDialog } from "./ProjectEditor/components";
import { usePlayback, useKeyboardShortcuts, useSimpleVideoPlayer } from "./ProjectEditor/hooks";
import { useAudioTrackPlayer } from "./ProjectEditor/hooks/useAudioTrackPlayer";
import { getAssetUrl } from "@/utils/studioUtils";
import { getEffectiveDuration } from "./ProjectEditor/utils/clipUtils";
import { uploadFileToS3 } from "@/utils/s3Upload";

import { getApiUrl } from '@/config/api';

const API = getApiUrl();

// Helper to format time as MM:SS;FF (frames at 24fps)
const formatTimecode = (ms) => {
  if (!ms && ms !== 0) return '00:00;00';
  // Ensure we're working with non-negative values
  const absMs = Math.max(0, ms);
  const totalSeconds = Math.floor(absMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const frames = Math.floor((absMs % 1000) / (1000 / 24));
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')};${String(frames).padStart(2, '0')}`;
};

export default function ProjectEditor() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = window.location;
  const { getAuthHeaders } = useAuth();
  
  // Parse URL params for navigation from Shot Editor
  const urlParams = new URLSearchParams(location.search);
  const targetShotId = urlParams.get('shot');
  const targetOrder = parseInt(urlParams.get('order') || '0', 10);
  
  // Core state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [editorState, setEditorState] = useState(null);
  const [shots, setShots] = useState([]);
  const [audioAssets, setAudioAssets] = useState([]);
  
  // Upload progress modal state
  const [uploadProgress, setUploadProgress] = useState(null); // { filename, stage, progress }
  
  // Track scene IDs that user has explicitly excluded from timeline
  // These won't be re-added during sync
  const [excludedSceneIds, setExcludedSceneIds] = useState([]);
  
  // Scene groupings (e.g., "INT - HOUSE - NIGHT")
  // Each scene contains shot IDs that belong to it
  const [sceneGroups, setSceneGroups] = useState([
    // Example structure:
    // { id: '1', name: 'INT - HOUSE - NIGHT', shotIds: ['shot1', 'shot2'] }
  ]);
  
  // Ref to track latest sceneGroups for autosave (avoids stale closure)
  const sceneGroupsRef = useRef(sceneGroups);
  useEffect(() => {
    sceneGroupsRef.current = sceneGroups;
  }, [sceneGroups]);
  
  // Ref to track excluded scene IDs for autosave
  const excludedSceneIdsRef = useRef(excludedSceneIds);
  useEffect(() => {
    excludedSceneIdsRef.current = excludedSceneIds;
  }, [excludedSceneIds]);
  
  // Timeline state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [selectedClipIds, setSelectedClipIds] = useState([]);
  const [selectedClipType, setSelectedClipType] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [mixerOpen, setMixerOpen] = useState(false);
  const [masterVolume, setMasterVolume] = useState(0); // dB
  const [reloadTrigger, setReloadTrigger] = useState(0); // Increment to force video reload
  const timelineRef = useRef(null);
  const playheadRef = useRef(null); // Direct ref to playhead for smooth updates
  const timecodeRef = useRef(null); // Direct ref to timecode display for smooth updates
  
  // History for undo/redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Autosave
  const autosaveTimeoutRef = useRef(null);
  const lastSavedStateRef = useRef(null);

  // Calculate total duration from video clips (accounting for speed)
  const totalDuration = editorState?.video_clips?.reduce((max, clip) => 
    Math.max(max, clip.start_ms + getEffectiveDuration(clip)), 0) || 0;

  // Video refs - main player and preload buffer
  const videoRef = useRef(null);
  const preloadVideoRef = useRef(null);
  // Real-time position ref for audio sync during playback
  const realtimePositionRef = useRef(0);

  // Use playback hook for controls
  const {
    isPlaying,
    playheadPosition,
    getPosition,
    play,
    pause,
    togglePlayPause,
    seek,
    stepForward,
    stepBackward,
    goToStart,
    goToEnd,
    skipForward,
    skipBackward,
    getClipAtPosition
  } = usePlayback(editorState?.video_clips, editorState?.audio_tracks, totalDuration, shots);

  // Callback for smooth playhead updates - updates DOM directly without state
  const updatePlayheadDirect = useCallback((positionMs) => {
    // Update realtime position ref for audio sync
    realtimePositionRef.current = positionMs;
    // Update playhead DOM directly for smooth animation
    if (playheadRef.current?.setPosition) {
      playheadRef.current.setPosition(positionMs);
    }
    // Update timecode display directly
    if (timecodeRef.current) {
      const totalMs = Math.max(0, positionMs);
      const hours = Math.floor(totalMs / 3600000);
      const minutes = Math.floor((totalMs % 3600000) / 60000);
      const seconds = Math.floor((totalMs % 60000) / 1000);
      const frames = Math.floor((totalMs % 1000) / (1000 / 30)); // Assuming 30fps
      timecodeRef.current.textContent = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}:${frames.toString().padStart(2, '0')}`;
    }
  }, []);

  // Track the current playing clip for UI display
  const [displayClip, setDisplayClip] = useState(null);

  // Simple video player - drives playhead from actual video time
  const { 
    currentClip: playerCurrentClip,
    isLoading: videoLoading,
    error: videoError,
    showBlackScreen,
    seekToPosition,
    loadClip
  } = useSimpleVideoPlayer({
    videoRef,
    preloadVideoRef,
    clips: editorState?.video_clips || [],
    shots,
    getAssetUrl,
    isPlaying,
    onPositionChange: updatePlayheadDirect, // Use direct DOM update
    onSeekComplete: seek, // Use state update only when seeking/pausing
    onClipChange: (clip) => {
      console.log('Playing clip:', clip?.id?.substring(0, 8));
      setDisplayClip(clip); // Update display clip for UI
    }
  });

  // Audio track playback - syncs separate audio clips with the timeline
  const { isLoading: isAudioLoading, loadingProgress: audioLoadingProgress } = useAudioTrackPlayer({
    audioTracks: editorState?.audio_tracks || [],
    audioAssets,
    isPlaying,
    playheadPosition,
    getAssetUrl,
    positionRef: realtimePositionRef  // Pass ref for real-time position during playback
  });

  // Get selected clip data
  const selectedClip = selectedClipIds.length === 1 
    ? (selectedClipType === 'video' 
        ? editorState?.video_clips?.find(c => c.id === selectedClipIds[0])
        : editorState?.audio_tracks?.flatMap(t => t.clips)?.find(c => c.id === selectedClipIds[0]))
    : null;

  const selectedShot = selectedClip?.scene_id 
    ? shots.find(s => s.id === selectedClip.scene_id)
    : null;

  // Current clip and shot at playhead for display purposes
  // During playback, use displayClip from video player; when paused, use calculated position
  const currentClip = useMemo(() => {
    if (isPlaying && displayClip) {
      return displayClip;
    }
    return getClipAtPosition(playheadPosition);
  }, [isPlaying, displayClip, playheadPosition, getClipAtPosition]);
  
  const currentShot = useMemo(() => {
    return currentClip?.scene_id ? shots.find(s => s.id === currentClip.scene_id) : null;
  }, [currentClip?.scene_id, shots]);

  // Seek video when user scrubs timeline (not during playback)
  useEffect(() => {
    if (!isPlaying && seekToPosition) {
      seekToPosition(playheadPosition);
    }
  }, [playheadPosition, isPlaying, seekToPosition]);

  // Force reload current clip when reloadTrigger changes (e.g., after take change)
  useEffect(() => {
    if (reloadTrigger > 0 && loadClip) {
      // Find which clip the playhead is currently on
      const clips = editorState?.video_clips || [];
      const clipIndex = clips.findIndex(clip => {
        const clipEnd = clip.start_ms + getEffectiveDuration(clip);
        return playheadPosition >= clip.start_ms && playheadPosition < clipEnd;
      });
      if (clipIndex >= 0) {
        loadClip(clipIndex);
      }
    }
  }, [reloadTrigger, loadClip, editorState?.video_clips, playheadPosition]);

  // Apply clip settings to video element when they change
  useEffect(() => {
    if (!videoRef.current || !currentClip) return;
    const settings = currentClip.settings || {};
    // HTML5 volume only accepts [0, 1] - clamp values over 100% to 1
    videoRef.current.volume = Math.max(0, Math.min(1, settings.volume ?? 1));
    videoRef.current.playbackRate = Math.max(0.25, Math.min(2, settings.speed ?? 1));
  }, [currentClip?.settings?.volume, currentClip?.settings?.speed]);

  // Navigate to specific shot when coming from Shot Editor
  useEffect(() => {
    if (!loading && targetShotId && editorState?.video_clips) {
      // Find the clip for this shot
      const clipIndex = editorState.video_clips.findIndex(c => c.scene_id === targetShotId);
      if (clipIndex >= 0) {
        const clip = editorState.video_clips[clipIndex];
        // Seek to the beginning of this shot
        seek(clip.start_ms);
        // Select this clip
        setSelectedClipIds([clip.id]);
        setSelectedClipType('video');
        // Clear the URL params
        window.history.replaceState({}, '', `/project/${projectId}/editor`);
      }
    }
  }, [loading, targetShotId, editorState?.video_clips, projectId]);

  // Load initial data
  useEffect(() => {
    loadData();
    
    // Warn before leaving
    const handleBeforeUnload = (e) => {
      if (autosaveTimeoutRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [projectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load project info
      const projectRes = await axios.get(`${API}/projects/${projectId}`, {
        headers: getAuthHeaders()
      });
      setProject(projectRes.data);
      
      // Load editor state
      const editorRes = await axios.get(`${API}/projects/${projectId}/editor`, {
        headers: getAuthHeaders()
      });
      
      const state = editorRes.data.state;
      setEditorState(state);
      setZoomLevel(state.zoom_level || 1);
      setSceneGroups(state.scene_groups || []);
      setExcludedSceneIds(state.excluded_scene_ids || []);
      lastSavedStateRef.current = JSON.stringify(state);
      setHistory([state]);
      setHistoryIndex(0);
      
      // Load shots with takes
      const shotsRes = await axios.get(`${API}/projects/${projectId}/editor/scenes-with-takes`, {
        headers: getAuthHeaders()
      });
      const loadedShots = shotsRes.data.scenes || [];
      setShots(loadedShots);
      
      // Debug: Log shots data to help diagnose video loading issues
      console.log('[ProjectEditor] Loaded shots:', loadedShots.length);
      loadedShots.forEach((s, i) => {
        console.log(`  Shot ${i}: ${s.name}`, {
          id: s.id?.substring(0, 8),
          printed_take_id: s.printed_take_id?.substring(0, 8) || 'none',
          takes: s.takes?.length || 0,
          hasVideoUrls: s.takes?.some(t => t.video_url) || false
        });
      });
      
      // Load audio assets
      const audioRes = await axios.get(`${API}/projects/${projectId}/editor/audio-assets`, {
        headers: getAuthHeaders()
      });
      setAudioAssets(audioRes.data.assets || []);
      
      // Initialize if new, otherwise sync to catch any scene changes
      if (editorRes.data.is_new) {
        await initializeFromShots();
      } else {
        // Auto-sync timeline with scenes (handles added/deleted scenes)
        await syncTimelineWithScenes();
      }
      
    } catch (error) {
      console.error('Failed to load:', error);
      toast.error('Failed to load project editor');
    } finally {
      setLoading(false);
    }
  };

  const initializeFromShots = async () => {
    try {
      const res = await axios.post(`${API}/projects/${projectId}/editor/initialize`, {}, {
        headers: getAuthHeaders()
      });
      setEditorState(res.data.state);
      toast.success(`Initialized timeline with ${res.data.clips_created} shots`);
    } catch (error) {
      console.error('Failed to initialize:', error);
    }
  };

  const syncTimelineWithScenes = async () => {
    try {
      const res = await axios.post(`${API}/projects/${projectId}/editor/sync`, {}, {
        headers: getAuthHeaders()
      });
      if (res.data.synced) {
        // Reload editor state to get updated clips
        const editorRes = await axios.get(`${API}/projects/${projectId}/editor`, {
          headers: getAuthHeaders()
        });
        const state = editorRes.data.state || editorRes.data;
        setEditorState(state);
        
        const changes = [];
        if (res.data.added_clips > 0) changes.push(`${res.data.added_clips} added`);
        if (res.data.removed_clips > 0) changes.push(`${res.data.removed_clips} removed`);
        console.log(`[ProjectEditor] Timeline synced: ${changes.join(', ')} clips`);
      }
    } catch (error) {
      console.error('Failed to sync timeline:', error);
    }
  };

  // Autosave
  const triggerAutosave = useCallback(() => {
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }
    
    autosaveTimeoutRef.current = setTimeout(async () => {
      if (!editorState) return;
      
      // Use refs to get latest values (avoids stale closure)
      const currentSceneGroups = sceneGroupsRef.current;
      const currentExcludedSceneIds = excludedSceneIdsRef.current;
      const currentStateStr = JSON.stringify({ 
        editorState, 
        sceneGroups: currentSceneGroups,
        excludedSceneIds: currentExcludedSceneIds 
      });
      if (currentStateStr === lastSavedStateRef.current) return;
      
      try {
        setSaving(true);
        await axios.put(`${API}/projects/${projectId}/editor`, {
          video_clips: editorState.video_clips,
          audio_tracks: editorState.audio_tracks,
          scene_groups: currentSceneGroups,
          excluded_scene_ids: currentExcludedSceneIds,
          playhead_position_ms: playheadPosition,
          zoom_level: zoomLevel
        }, {
          headers: getAuthHeaders()
        });
        lastSavedStateRef.current = currentStateStr;
      } catch (error) {
        console.error('Autosave failed:', error);
      } finally {
        setSaving(false);
        autosaveTimeoutRef.current = null;
      }
    }, 3000);
  }, [editorState, playheadPosition, zoomLevel, projectId, getAuthHeaders]);

  // Update state with history
  const updateEditorState = useCallback((newState) => {
    setEditorState(newState);
    setHistory(h => [...h.slice(0, historyIndex + 1), newState].slice(-50));
    setHistoryIndex(i => Math.min(i + 1, 49));
    triggerAutosave();
  }, [historyIndex, triggerAutosave]);

  // Undo/Redo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(i => i - 1);
      setEditorState(history[historyIndex - 1]);
      triggerAutosave();
    }
  }, [history, historyIndex, triggerAutosave]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(i => i + 1);
      setEditorState(history[historyIndex + 1]);
      triggerAutosave();
    }
  }, [history, historyIndex, triggerAutosave]);

  // Select clip
  const handleSelectClip = useCallback((clipId, type, addToSelection = false) => {
    if (addToSelection) {
      setSelectedClipIds(prev => 
        prev.includes(clipId) ? prev.filter(id => id !== clipId) : [...prev, clipId]
      );
    } else {
      setSelectedClipIds([clipId]);
    }
    setSelectedClipType(type);
  }, []);

  // Move clip
  const handleMoveClip = useCallback((clipId, type, newStartMs, trackId) => {
    if (type === 'video') {
      updateEditorState({
        ...editorState,
        video_clips: editorState.video_clips.map(clip => 
          clip.id === clipId ? { ...clip, start_ms: Math.round(newStartMs) } : clip
        )
      });
    } else {
      updateEditorState({
        ...editorState,
        audio_tracks: editorState.audio_tracks.map(track => ({
          ...track,
          clips: track.clips.map(clip => 
            clip.id === clipId ? { ...clip, start_ms: Math.round(newStartMs) } : clip
          )
        }))
      });
    }
  }, [editorState, updateEditorState]);

  // Trim clip
  const handleTrimClip = useCallback((clipId, type, trimUpdates, trackId) => {
    if (type === 'video') {
      updateEditorState({
        ...editorState,
        video_clips: editorState.video_clips.map(clip => 
          clip.id === clipId ? { ...clip, ...trimUpdates } : clip
        )
      });
    } else {
      updateEditorState({
        ...editorState,
        audio_tracks: editorState.audio_tracks.map(track => 
          track.id === trackId
            ? {
                ...track,
                clips: track.clips.map(clip => 
                  clip.id === clipId ? { ...clip, ...trimUpdates } : clip
                )
              }
            : track
        )
      });
    }
  }, [editorState, updateEditorState]);

  // Reorder video clips
  const handleReorderClips = useCallback((clipId, newIndex) => {
    const clips = [...editorState.video_clips];
    const currentIndex = clips.findIndex(c => c.id === clipId);
    if (currentIndex === -1 || currentIndex === newIndex) return;
    
    // Remove clip from current position
    const [clip] = clips.splice(currentIndex, 1);
    // Insert at new position
    clips.splice(newIndex > currentIndex ? newIndex - 1 : newIndex, 0, clip);
    
    // Recalculate start times to be sequential with no gaps
    let currentTime = 0;
    const reorderedClips = clips.map(c => {
      const effectiveDuration = c.duration_ms - (c.trim_start_ms || 0) - (c.trim_end_ms || 0);
      const updatedClip = { ...c, start_ms: currentTime };
      currentTime += effectiveDuration;
      return updatedClip;
    });
    
    updateEditorState({
      ...editorState,
      video_clips: reorderedClips
    });
    
    toast.success('Clips reordered');
  }, [editorState, updateEditorState]);

  // Drop audio - gets actual duration from the audio file
  const handleDropAudio = useCallback(async (trackId, asset, startMs) => {
    // First, try to get the actual duration from the audio file
    let durationMs = 30000; // Default fallback
    
    try {
      const audioUrl = getAssetUrl(asset.file_url);
      if (audioUrl) {
        // Use an Audio element to get the duration
        const audio = new Audio();
        audio.preload = 'metadata';
        
        durationMs = await new Promise((resolve) => {
          const timeout = setTimeout(() => {
            console.log('Audio duration fetch timeout, using default');
            resolve(30000);
          }, 5000);
          
          audio.onloadedmetadata = () => {
            clearTimeout(timeout);
            const duration = audio.duration;
            if (duration && isFinite(duration)) {
              resolve(Math.round(duration * 1000));
            } else {
              resolve(30000);
            }
          };
          
          audio.onerror = () => {
            clearTimeout(timeout);
            console.log('Error loading audio metadata, using default duration');
            resolve(30000);
          };
          
          audio.src = audioUrl;
        });
      }
    } catch (err) {
      console.error('Failed to get audio duration:', err);
    }
    
    const newClip = {
      id: crypto.randomUUID(),
      asset_id: asset.id,
      asset_name: asset.name,
      asset_url: asset.file_url, // Store the URL for playback
      start_ms: Math.round(startMs),
      duration_ms: durationMs,
      trim_start_ms: 0,
      trim_end_ms: 0,
      settings: { active: true, volume: 1.0, speed: 1.0, pan: 0 }
    };
    
    updateEditorState({
      ...editorState,
      audio_tracks: editorState.audio_tracks.map(track => 
        track.id === trackId 
          ? { ...track, clips: [...track.clips, newClip] }
          : track
      )
    });
    toast.success(`Added "${asset.name}" to timeline`);
  }, [editorState, updateEditorState, getAssetUrl]);

  // Auto-correct audio clip duration when actual duration is detected
  const handleAudioDurationDetected = useCallback((clipId, actualDurationMs) => {
    if (!editorState) return;
    
    console.log(`Correcting audio clip ${clipId} duration to ${actualDurationMs}ms`);
    
    updateEditorState({
      ...editorState,
      audio_tracks: editorState.audio_tracks.map(track => ({
        ...track,
        clips: track.clips.map(clip => 
          clip.id === clipId 
            ? { ...clip, duration_ms: actualDurationMs }
            : clip
        )
      }))
    });
  }, [editorState, updateEditorState]);

  // Update clip settings (called from SettingsPanel with clipId and type)
  const handleUpdateClipSettings = useCallback((clipId, clipType, settings) => {
    if (!clipId || !editorState) return;
    
    if (clipType === 'video') {
      updateEditorState({
        ...editorState,
        video_clips: editorState.video_clips.map(clip => 
          clip.id === clipId 
            ? { ...clip, settings: { ...(clip.settings || {}), ...settings } }
            : clip
        )
      });
    } else {
      updateEditorState({
        ...editorState,
        audio_tracks: editorState.audio_tracks.map(track => ({
          ...track,
          clips: track.clips.map(clip => 
            clip.id === clipId 
              ? { ...clip, settings: { ...(clip.settings || {}), ...settings } }
              : clip
          )
        }))
      });
    }
  }, [editorState, updateEditorState]);

  // Delete clip
  const handleDeleteClip = useCallback((clipId, clipType) => {
    if (!clipId || !editorState) return;
    
    if (clipType === 'video') {
      const clipToDelete = editorState.video_clips.find(c => c.id === clipId);
      const newClips = editorState.video_clips.filter(clip => clip.id !== clipId);
      
      // Track excluded scene ID so sync won't re-add it
      if (clipToDelete?.scene_id && !clipToDelete.is_blank) {
        setExcludedSceneIds(prev => [...new Set([...prev, clipToDelete.scene_id])]);
      }
      
      // Recalculate start times to close gaps
      let currentStart = 0;
      const adjustedClips = newClips.map(clip => {
        const adjusted = { ...clip, start_ms: currentStart };
        currentStart += clip.duration_ms - (clip.trim_start_ms || 0) - (clip.trim_end_ms || 0);
        return adjusted;
      });
      
      updateEditorState({
        ...editorState,
        video_clips: adjustedClips
      });
      setSelectedClipIds([]);
      toast.success('Clip deleted');
    } else {
      updateEditorState({
        ...editorState,
        audio_tracks: editorState.audio_tracks.map(track => ({
          ...track,
          clips: track.clips.filter(clip => clip.id !== clipId)
        }))
      });
      setSelectedClipIds([]);
      toast.success('Audio clip deleted');
    }
  }, [editorState, updateEditorState]);

  // Change take
  const handleChangeTake = useCallback(async (sceneId, takeId) => {
    try {
      // Update backend - note: API still uses 'scenes' endpoint
      await axios.put(
        `${API}/projects/${projectId}/editor/scenes/${sceneId}/printed-take?take_id=${takeId}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      // Update local state - shots first so video player has new data
      setShots(prev => prev.map(s => 
        s.id === sceneId ? { ...s, printed_take_id: takeId } : s
      ));
      
      // Update video clip with new take
      const updatedClips = editorState.video_clips.map(clip => 
        clip.scene_id === sceneId ? { ...clip, take_id: takeId } : clip
      );
      
      updateEditorState({
        ...editorState,
        video_clips: updatedClips
      });
      
      // Trigger reload after a brief delay to let state propagate
      setTimeout(() => {
        setReloadTrigger(prev => prev + 1);
      }, 50);
      
      toast.success('Take updated');
    } catch (error) {
      toast.error('Failed to update take');
    }
  }, [projectId, editorState, updateEditorState, getAuthHeaders]);

  // Edit shot (navigate to shot editor)
  const handleEditShot = useCallback((sceneId) => {
    navigate(`/project/${projectId}/studio/${sceneId}`);
  }, [projectId, navigate]);

  // Mixer handlers
  const handleTrackVolumeChange = useCallback((trackId, volumeDb) => {
    if (!editorState) return;
    updateEditorState({
      ...editorState,
      audio_tracks: editorState.audio_tracks.map(track =>
        track.id === trackId ? { ...track, volume: volumeDb } : track
      )
    });
  }, [editorState, updateEditorState]);

  const handleTrackPanChange = useCallback((trackId, pan) => {
    if (!editorState) return;
    updateEditorState({
      ...editorState,
      audio_tracks: editorState.audio_tracks.map(track =>
        track.id === trackId ? { ...track, pan: pan } : track
      )
    });
  }, [editorState, updateEditorState]);

  const handleTrackMuteToggle = useCallback((trackId) => {
    if (!editorState) return;
    updateEditorState({
      ...editorState,
      audio_tracks: editorState.audio_tracks.map(track =>
        track.id === trackId ? { ...track, muted: !track.muted } : track
      )
    });
  }, [editorState, updateEditorState]);

  // Scene Group Management
  const createSceneGroup = useCallback((name, shotIds = []) => {
    const newGroup = {
      id: `scene-${Date.now()}`,
      name: name || 'New Scene',
      shotIds: shotIds,
      color: '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')
    };
    setSceneGroups(prev => [...prev, newGroup]);
    triggerAutosave();
    return newGroup;
  }, [triggerAutosave]);

  const updateSceneGroup = useCallback((groupId, updates) => {
    setSceneGroups(prev => prev.map(g => 
      g.id === groupId ? { ...g, ...updates } : g
    ));
    triggerAutosave();
  }, [triggerAutosave]);

  const deleteSceneGroup = useCallback((groupId) => {
    setSceneGroups(prev => prev.filter(g => g.id !== groupId));
    triggerAutosave();
  }, [triggerAutosave]);

  const addShotToSceneGroup = useCallback((groupId, shotId) => {
    setSceneGroups(prev => prev.map(g => 
      g.id === groupId 
        ? { ...g, shotIds: [...new Set([...g.shotIds, shotId])] }
        : g
    ));
    triggerAutosave();
  }, [triggerAutosave]);

  const removeShotFromSceneGroup = useCallback((groupId, shotId) => {
    setSceneGroups(prev => prev.map(g => 
      g.id === groupId 
        ? { ...g, shotIds: g.shotIds.filter(id => id !== shotId) }
        : g
    ));
    triggerAutosave();
  }, [triggerAutosave]);

  // Get scene group for a shot
  const getSceneGroupForShot = useCallback((shotId) => {
    return sceneGroups.find(g => g.shotIds.includes(shotId));
  }, [sceneGroups]);

  // Split at playhead
  const splitAtPlayhead = useCallback(() => {
    if (selectedClipIds.length === 0) {
      toast.error('Select a clip to split');
      return;
    }
    
    // Find the selected clip(s) that the playhead is over
    const clipsToSplit = [];
    
    if (selectedClipType === 'video') {
      const clip = editorState.video_clips.find(c => 
        selectedClipIds.includes(c.id) &&
        playheadPosition > c.start_ms &&
        playheadPosition < c.start_ms + c.duration_ms - (c.trim_start_ms || 0) - (c.trim_end_ms || 0)
      );
      if (clip) clipsToSplit.push({ clip, type: 'video' });
    } else {
      for (const track of editorState.audio_tracks) {
        const clip = track.clips.find(c => 
          selectedClipIds.includes(c.id) &&
          playheadPosition > c.start_ms &&
          playheadPosition < c.start_ms + c.duration_ms - (c.trim_start_ms || 0) - (c.trim_end_ms || 0)
        );
        if (clip) clipsToSplit.push({ clip, type: 'audio', trackId: track.id });
      }
    }
    
    if (clipsToSplit.length === 0) {
      toast.error('Playhead must be within the selected clip');
      return;
    }
    
    // Split each clip
    for (const { clip, type, trackId } of clipsToSplit) {
      const splitPoint = playheadPosition - clip.start_ms;
      const effectiveDuration = clip.duration_ms - (clip.trim_start_ms || 0) - (clip.trim_end_ms || 0);
      
      // First part - original clip trimmed at end
      const firstPart = {
        ...clip,
        trim_end_ms: (clip.trim_end_ms || 0) + (effectiveDuration - splitPoint)
      };
      
      // Second part - new clip starting after split
      const secondPart = {
        ...clip,
        id: crypto.randomUUID(),
        start_ms: playheadPosition,
        trim_start_ms: (clip.trim_start_ms || 0) + splitPoint,
        trim_end_ms: clip.trim_end_ms || 0
      };
      
      if (type === 'video') {
        updateEditorState({
          ...editorState,
          video_clips: [
            ...editorState.video_clips.filter(c => c.id !== clip.id),
            firstPart,
            secondPart
          ].sort((a, b) => a.start_ms - b.start_ms)
        });
      } else {
        updateEditorState({
          ...editorState,
          audio_tracks: editorState.audio_tracks.map(track => 
            track.id === trackId
              ? {
                  ...track,
                  clips: [
                    ...track.clips.filter(c => c.id !== clip.id),
                    firstPart,
                    secondPart
                  ].sort((a, b) => a.start_ms - b.start_ms)
                }
              : track
          )
        });
      }
    }
    
    toast.success('Clip split');
  }, [selectedClipIds, selectedClipType, playheadPosition, editorState, updateEditorState]);

  // Insert blank after the shot that contains the playhead
  const insertBlank = useCallback(() => {
    if (!editorState?.video_clips?.length) {
      // If no clips, just insert at position 0
      const blankClip = {
        id: crypto.randomUUID(),
        scene_id: null,
        take_id: null,
        is_blank: true,
        start_ms: 0,
        duration_ms: 3000,
        trim_start_ms: 0,
        trim_end_ms: 0,
        settings: { active: true, volume: 1.0, speed: 1.0, transparency: 1.0, scale: 1.0, position: { x: 0, y: 0 } }
      };
      updateEditorState({ ...editorState, video_clips: [blankClip] });
      toast.success('Blank clip inserted');
      return;
    }
    
    // Sort clips by start time
    const sortedClips = [...editorState.video_clips].sort((a, b) => a.start_ms - b.start_ms);
    
    // Find which clip the playhead is currently on (or after)
    let insertAfterIndex = -1;
    for (let i = 0; i < sortedClips.length; i++) {
      const clip = sortedClips[i];
      const clipEnd = clip.start_ms + clip.duration_ms - (clip.trim_start_ms || 0) - (clip.trim_end_ms || 0);
      if (playheadPosition >= clip.start_ms) {
        insertAfterIndex = i;
      }
    }
    
    // Calculate where to insert the blank (after the current clip)
    const blankDuration = 3000; // 3 seconds default
    let insertPosition = 0;
    
    if (insertAfterIndex >= 0) {
      const clipAfter = sortedClips[insertAfterIndex];
      insertPosition = clipAfter.start_ms + clipAfter.duration_ms - (clipAfter.trim_start_ms || 0) - (clipAfter.trim_end_ms || 0);
    }
    
    // Create the blank clip
    const blankClip = {
      id: crypto.randomUUID(),
      scene_id: null,
      take_id: null,
      is_blank: true,
      start_ms: insertPosition,
      duration_ms: blankDuration,
      trim_start_ms: 0,
      trim_end_ms: 0,
      settings: { active: true, volume: 1.0, speed: 1.0, transparency: 1.0, scale: 1.0, position: { x: 0, y: 0 } }
    };
    
    // Shift all clips after the insert position
    const newClips = sortedClips.map((clip, index) => {
      if (index > insertAfterIndex) {
        return { ...clip, start_ms: clip.start_ms + blankDuration };
      }
      return clip;
    });
    
    // Insert the blank clip
    newClips.splice(insertAfterIndex + 1, 0, blankClip);
    
    updateEditorState({
      ...editorState,
      video_clips: newClips
    });
    toast.success('Blank clip inserted');
  }, [playheadPosition, editorState, updateEditorState]);

  // Re-add an excluded shot back to the timeline
  const handleReAddShot = useCallback(async (sceneId) => {
    if (!sceneId || !shots.length) return;
    
    // Find the shot/scene
    const scene = shots.find(s => s.id === sceneId);
    if (!scene) {
      toast.error('Shot not found');
      return;
    }
    
    // Get duration from printed take if available
    let durationMs = 5000; // Default 5 seconds
    if (scene.printed_take_id) {
      const allTakes = [...(scene.animation_takes || []), ...(scene.lipsync_takes || [])];
      const printedTake = allTakes.find(t => t.id === scene.printed_take_id);
      if (printedTake?.duration) {
        durationMs = Math.round(printedTake.duration * 1000);
      }
    }
    
    // Calculate position at end of timeline
    const sortedClips = [...(editorState?.video_clips || [])].sort((a, b) => a.start_ms - b.start_ms);
    let endPosition = 0;
    for (const clip of sortedClips) {
      const clipEnd = clip.start_ms + clip.duration_ms - (clip.trim_start_ms || 0) - (clip.trim_end_ms || 0);
      if (clipEnd > endPosition) endPosition = clipEnd;
    }
    
    // Create new clip
    const newClip = {
      id: crypto.randomUUID(),
      scene_id: sceneId,
      take_id: scene.printed_take_id || null,
      is_blank: false,
      start_ms: endPosition,
      duration_ms: durationMs,
      trim_start_ms: 0,
      trim_end_ms: 0,
      settings: { active: true, volume: 1.0, speed: 1.0, transparency: 1.0, scale: 1.0, position: { x: 0, y: 0 } }
    };
    
    // Remove from excluded list and add clip
    setExcludedSceneIds(prev => prev.filter(id => id !== sceneId));
    updateEditorState({
      ...editorState,
      video_clips: [...sortedClips, newClip]
    });
    toast.success(`Added ${scene.name} back to timeline`);
  }, [shots, editorState, updateEditorState]);

  // Get list of excluded shots for UI display
  const excludedShots = useMemo(() => {
    return shots.filter(shot => excludedSceneIds.includes(shot.id));
  }, [shots, excludedSceneIds]);

  // Rename audio track
  const handleRenameTrack = useCallback(async (trackId, newName) => {
    if (!editorState?.audio_tracks) return;
    
    const updatedTracks = editorState.audio_tracks.map(track =>
      track.id === trackId ? { ...track, name: newName } : track
    );
    
    updateEditorState({
      ...editorState,
      audio_tracks: updatedTracks
    });
    toast.success('Track renamed');
  }, [editorState, updateEditorState]);

  // Upload audio files using direct S3 upload with progress tracking
  const handleUploadAudio = useCallback(async (files) => {
    for (const file of files) {
      try {
        // Show upload progress modal
        setUploadProgress({ filename: file.name, stage: 'uploading', progress: 0 });
        
        // Determine audio type based on filename hints
        const nameLower = file.name.toLowerCase();
        let audioType = 'music'; // Default to music for Project Editor uploads
        if (nameLower.includes('sfx') || nameLower.includes('sound') || nameLower.includes('effect')) {
          audioType = 'sfx';
        } else if (nameLower.includes('voice') || nameLower.includes('dialog') || nameLower.includes('narr')) {
          audioType = 'voice';
        }
        
        const asset = await uploadFileToS3(file, projectId, {
          type: audioType,
          name: file.name,
          onProgress: (progress) => {
            setUploadProgress(prev => ({
              ...prev,
              stage: progress < 100 ? 'uploading' : 'processing',
              progress
            }));
          }
        });
        
        // Update stage to show waveform generation
        setUploadProgress(prev => ({ ...prev, stage: 'encoding', progress: 100 }));
        
        // Small delay to show encoding message
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setAudioAssets(prev => [...prev, asset]);
        setUploadProgress(null);
        toast.success(`Uploaded: ${file.name}`);
      } catch (error) {
        console.error('Upload error:', error);
        setUploadProgress(null);
        
        // Provide specific error messages
        let errorMessage = `Failed to upload: ${file.name}`;
        if (error.response?.status === 413) {
          errorMessage = `File too large: ${file.name}. Max size is 50MB.`;
        } else if (error.response?.status === 415) {
          errorMessage = `Unsupported format: ${file.name}. Please use MP3, WAV, or AAC.`;
        } else if (error.response?.status === 520) {
          errorMessage = `Server error uploading ${file.name}. Please try again.`;
        } else if (error.message?.includes('network') || error.code === 'ERR_NETWORK') {
          errorMessage = `Network error uploading ${file.name}. Check your connection.`;
        } else if (error.response?.data?.detail) {
          errorMessage = `${file.name}: ${error.response.data.detail}`;
        }
        
        toast.error(errorMessage);
      }
    }
  }, [projectId]);

  // Import audio from another project
  const handleImportFromProject = useCallback(() => {
    toast.info('Import from project - Coming soon');
    // TODO: Implement project selection dialog and audio import
  }, []);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    isPlaying,
    togglePlayPause,
    stepForward,
    stepBackward,
    goToStart,
    goToEnd,
    skipForward,
    skipBackward,
    undo,
    redo,
    splitAtPlayhead
  });

  // Calculate timeline measurements
  const pxPerMs = (zoomLevel * 100) / 10000;
  const totalWidth = Math.max(totalDuration * pxPerMs, 800);
  const labelWidth = 96;

  // Follow playhead during playback - keep it visible in timeline
  useEffect(() => {
    if (!isPlaying) return;
    
    const playheadX = playheadPosition * pxPerMs;
    const viewportWidth = timelineRef.current?.clientWidth || 800;
    const margin = 100; // Keep playhead this many pixels from edge
    
    // Check if playhead is off-screen to the right
    if (playheadX > scrollLeft + viewportWidth - labelWidth - margin) {
      setScrollLeft(Math.max(0, playheadX - viewportWidth + labelWidth + margin * 2));
    }
    // Check if playhead is off-screen to the left
    else if (playheadX < scrollLeft + margin) {
      setScrollLeft(Math.max(0, playheadX - margin));
    }
  }, [isPlaying, playheadPosition, pxPerMs, scrollLeft, labelWidth]);

  // Track previous isPlaying state to detect when playback starts
  const wasPlayingRef = useRef(false);
  
  // Scroll to playhead when play starts (not during continuous playback)
  useEffect(() => {
    // Only scroll to playhead when transitioning from paused to playing
    if (isPlaying && !wasPlayingRef.current) {
      const playheadX = playheadPosition * pxPerMs;
      const viewportWidth = timelineRef.current?.clientWidth || 800;
      
      // If playhead is not visible, scroll to center it
      if (playheadX < scrollLeft || playheadX > scrollLeft + viewportWidth - labelWidth) {
        setScrollLeft(Math.max(0, playheadX - (viewportWidth - labelWidth) / 2));
      }
    }
    wasPlayingRef.current = isPlaying;
  }, [isPlaying, playheadPosition, pxPerMs, scrollLeft, labelWidth]);

  // Prevent browser back/forward on horizontal scroll in timeline
  useEffect(() => {
    const timeline = timelineRef.current;
    if (!timeline) return;
    
    const handleWheel = (e) => {
      // If there's any horizontal scroll component, prevent default to avoid browser navigation
      if (e.deltaX !== 0) {
        e.preventDefault();
      }
    };
    
    // Must use passive: false to be able to preventDefault
    timeline.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      timeline.removeEventListener('wheel', handleWheel);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-4" />
          <p className="text-zinc-400">Loading Project Editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#09090b] flex flex-col overflow-hidden" data-testid="project-editor">
      {/* Header */}
      <header className="h-14 border-b border-zinc-800 bg-zinc-900/50 flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/project/${projectId}`)}
            className="text-zinc-400 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold text-white">{project?.name}</h1>
            <p className="text-xs text-zinc-500">Project Editor</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {saving && (
            <span className="text-xs text-zinc-500 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Saving...
            </span>
          )}
          
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={undo}
            disabled={historyIndex <= 0}
            className="text-zinc-400 hover:text-white disabled:opacity-30"
            title="Undo (⌘Z)"
          >
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={redo}
            disabled={historyIndex >= history.length - 1}
            className="text-zinc-400 hover:text-white disabled:opacity-30"
            title="Redo (⌘⇧Z)"
          >
            <Redo2 className="w-4 h-4" />
          </Button>
          
          <div className="w-px h-6 bg-zinc-700 mx-2" />
          
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
            onClick={() => setExportDialogOpen(true)}
            data-testid="export-btn"
          >
            <Download className="w-4 h-4" />
            Export
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Asset Library */}
        <div className="w-64 border-r border-zinc-800 bg-zinc-900/30 shrink-0 flex flex-col">
          {/* Mixer Button */}
          <div className="p-2 border-b border-zinc-800">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMixerOpen(true)}
              className="w-full bg-zinc-800 hover:bg-zinc-700 border-zinc-700 text-zinc-200"
              data-testid="open-mixer-btn"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="4" y="2" width="4" height="20" rx="1" />
                <rect x="10" y="6" width="4" height="16" rx="1" />
                <rect x="16" y="10" width="4" height="12" rx="1" />
              </svg>
              Open Mixer
            </Button>
          </div>
          <div className="flex-1 overflow-hidden">
            <AssetLibrary 
              audioAssets={audioAssets} 
              getAssetUrl={getAssetUrl}
              onUploadAudio={handleUploadAudio}
              onImportFromProject={handleImportFromProject}
            />
          </div>
        </div>

        {/* Center - Canvas + Timeline */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Video Preview */}
          <div className="flex-1 flex items-center justify-center bg-black/50 p-4 min-h-0">
            <div 
              className="relative bg-black rounded-lg overflow-hidden shadow-2xl flex items-center justify-center"
              style={{ 
                aspectRatio: '16/9',
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: '100%'
              }}
            >
              {/* Video player - simple direct playback */}
              <video
                ref={videoRef}
                className="max-w-full max-h-full"
                style={{ 
                  objectFit: 'contain',
                  background: '#000',
                  width: '100%',
                  height: '100%',
                  display: showBlackScreen ? 'none' : 'block',
                  opacity: currentClip?.settings?.transparency ?? 1,
                  transform: `scale(${currentClip?.settings?.scale ?? 1})`
                }}
                playsInline
              />
              
              {/* Hidden preload video for seamless transitions */}
              <video
                ref={preloadVideoRef}
                style={{ display: 'none' }}
                playsInline
                preload="auto"
              />
              
              {/* Black screen for clips without video */}
              {showBlackScreen && (
                <div className="absolute inset-0 bg-black" />
              )}
              
              {/* Loading overlay - only show during initial load, not transitions */}
              {videoLoading && !isPlaying && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                </div>
              )}
              
              {/* Error overlay */}
              {videoError && !showBlackScreen && (
                <div className="absolute inset-0 flex items-center justify-center bg-black">
                  <span className="text-zinc-500">
                    {videoError === 'blank' ? '' : videoError === 'no-video' ? 'No video available' : 'Failed to load video'}
                  </span>
                </div>
              )}
              
              {/* Shot name overlay */}
              {currentShot && (
                <div className="absolute bottom-4 left-4 bg-black/70 px-3 py-1.5 rounded text-sm text-white">
                  {currentShot.name}
                </div>
              )}
            </div>
          </div>

          {/* Transport Controls */}
          <div className="h-14 border-t border-zinc-800 bg-zinc-900/50 flex items-center justify-between px-4 shrink-0">
            <div className="flex items-center gap-3 w-52">
              <Clock className="w-4 h-4 text-zinc-500" />
              <div className="font-mono text-sm">
                <span ref={timecodeRef} className="text-white">{formatTimecode(playheadPosition)}</span>
                <span className="text-zinc-500"> / </span>
                <span className="text-zinc-400">{formatTimecode(totalDuration)}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={goToStart} className="text-zinc-400 hover:text-white h-8 w-8">
                <SkipBack className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => skipBackward(5000)} className="text-zinc-400 hover:text-white h-8 w-8">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              <Button
                size="icon"
                onClick={togglePlayPause}
                className="w-10 h-10 bg-indigo-600 hover:bg-indigo-700 rounded-full mx-2"
                data-testid="play-pause-btn"
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </Button>
              
              {/* Audio loading indicator */}
              {isAudioLoading && (
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-zinc-800 px-3 py-1 rounded text-xs text-zinc-300 flex items-center gap-2 whitespace-nowrap">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Loading audio ({audioLoadingProgress.loaded}/{audioLoadingProgress.total})
                </div>
              )}
              
              <Button variant="ghost" size="icon" onClick={() => skipForward(5000)} className="text-zinc-400 hover:text-white h-8 w-8">
                <ChevronRight className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={goToEnd} className="text-zinc-400 hover:text-white h-8 w-8">
                <SkipForward className="w-4 h-4" />
              </Button>
              
              <div className="w-px h-6 bg-zinc-700 mx-3" />
              
              <Button
                variant="ghost"
                size="sm"
                onClick={insertBlank}
                className="text-zinc-400 hover:text-white h-8 gap-1 text-xs"
                title="Insert blank clip"
              >
                <Plus className="w-3.5 h-3.5" />
                Blank
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={splitAtPlayhead}
                disabled={selectedClipIds.length === 0}
                className="text-zinc-400 hover:text-white h-8 gap-1 text-xs disabled:opacity-30"
                title="Split at playhead (⌘B)"
              >
                <Scissors className="w-3.5 h-3.5" />
                Split
              </Button>
            </div>
            
            <div className="flex items-center gap-2 w-52 justify-end">
              <Button variant="ghost" size="icon" onClick={() => setZoomLevel(z => Math.max(0.1, z - 0.2))} className="text-zinc-400 hover:text-white h-8 w-8">
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span 
                className="text-xs text-zinc-400 w-14 text-center cursor-pointer hover:text-white hover:bg-zinc-800 rounded px-1 py-0.5"
                onDoubleClick={(e) => {
                  const input = document.createElement('input');
                  input.type = 'number';
                  input.value = Math.round(zoomLevel * 100);
                  input.className = 'w-14 text-xs text-center bg-zinc-800 text-white rounded px-1 py-0.5 outline-none border border-zinc-600';
                  input.style.cssText = 'appearance: textfield; -moz-appearance: textfield;';
                  const span = e.currentTarget;
                  span.style.display = 'none';
                  span.parentNode.insertBefore(input, span.nextSibling);
                  input.focus();
                  input.select();
                  let finished = false;
                  const finish = () => {
                    if (finished) return;
                    finished = true;
                    const val = parseInt(input.value) || 100;
                    setZoomLevel(Math.max(10, Math.min(3000, val)) / 100);
                    if (input.parentNode) input.remove();
                    span.style.display = '';
                  };
                  input.onblur = finish;
                  input.onkeydown = (ev) => { 
                    if (ev.key === 'Enter') { ev.preventDefault(); finish(); }
                    if (ev.key === 'Escape') { finished = true; if (input.parentNode) input.remove(); span.style.display = ''; }
                  };
                }}
                title="Double-click to type zoom level"
              >
                {Math.round(zoomLevel * 100)}%
              </span>
              <Button variant="ghost" size="icon" onClick={() => setZoomLevel(z => Math.min(30, z + 0.2))} className="text-zinc-400 hover:text-white h-8 w-8">
                <ZoomIn className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Timeline */}
          <div 
            ref={timelineRef}
            className="h-72 border-t border-zinc-800 bg-zinc-950 flex flex-col shrink-0 overflow-hidden"
            style={{ 
              overscrollBehaviorX: 'none',  // Prevent browser back/forward on overscroll
              touchAction: 'pan-y pinch-zoom'  // Only allow vertical pan and pinch
            }}
            onWheel={(e) => {
              // Horizontal scroll with shift+wheel or trackpad horizontal swipe
              // Note: preventDefault is handled by native event listener for browser nav prevention
              if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
                const delta = e.shiftKey ? e.deltaY : e.deltaX;
                setScrollLeft(prev => Math.max(0, Math.min(prev + delta, totalWidth - 500)));
              }
            }}
          >
            {/* Time Ruler - clickable to position playhead */}
            <TimeRuler
              totalDuration={totalDuration}
              pxPerMs={pxPerMs}
              totalWidth={totalWidth}
              labelWidth={labelWidth}
              playheadPosition={playheadPosition}
              scrollLeft={scrollLeft}
              onSeek={seek}
            />
            
            {/* Tracks container with horizontal scroll */}
            <div className="flex-1 overflow-auto" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
              <div 
                className="relative"
                style={{ 
                  width: totalWidth + labelWidth,
                  transform: `translateX(-${scrollLeft}px)`
                }}
              >
                {/* Video Track */}
                <VideoTrack
                  clips={editorState?.video_clips || []}
                  shots={shots}
                  totalDuration={totalDuration}
                  pxPerMs={pxPerMs}
                  totalWidth={totalWidth}
                  labelWidth={labelWidth}
                  playheadPosition={playheadPosition}
                  selectedClipIds={selectedClipIds}
                  onSelectClip={handleSelectClip}
                  onMoveClip={handleMoveClip}
                  onTrimClip={handleTrimClip}
                  onReorderClips={handleReorderClips}
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const clickX = e.clientX - rect.left - labelWidth + scrollLeft;
                    seek(Math.max(0, clickX / pxPerMs));
                  }}
                  getAssetUrl={getAssetUrl}
                />
                
                {/* Video Audio Track - shows audio waveforms for video clips */}
                <VideoAudioTrack
                  clips={editorState?.video_clips || []}
                  shots={shots}
                  pxPerMs={pxPerMs}
                  totalWidth={totalWidth}
                  labelWidth={labelWidth}
                  selectedClipIds={selectedClipIds}
                  onSelectClip={handleSelectClip}
                  getAssetUrl={getAssetUrl}
                />
                
                {/* Audio Tracks */}
                {editorState?.audio_tracks?.map((track, index) => (
                  <AudioTrack
                    key={track.id}
                    track={track}
                    trackIndex={index}
                    totalDuration={totalDuration}
                    pxPerMs={pxPerMs}
                    totalWidth={totalWidth}
                    labelWidth={labelWidth}
                    playheadPosition={playheadPosition}
                    selectedClipIds={selectedClipIds}
                    audioAssets={audioAssets}
                    onSelectClip={handleSelectClip}
                    onMoveClip={handleMoveClip}
                    onTrimClip={handleTrimClip}
                    onDropAudio={handleDropAudio}
                    onUpdateTrack={() => {}}
                    onRenameTrack={handleRenameTrack}
                    onAudioDurationDetected={handleAudioDurationDetected}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const clickX = e.clientX - rect.left - labelWidth + scrollLeft;
                      seek(Math.max(0, clickX / pxPerMs));
                    }}
                    getAssetUrl={getAssetUrl}
                  />
                ))}
                
                {/* Playhead - draggable */}
                <Playhead
                  ref={playheadRef}
                  position={playheadPosition}
                  pxPerMs={pxPerMs}
                  labelWidth={labelWidth}
                  scrollLeft={scrollLeft}
                  totalDuration={totalDuration}
                  onSeek={seek}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Settings */}
        <div className="w-72 border-l border-zinc-800 bg-zinc-900/30 shrink-0">
          <SettingsPanel
            selectedClip={selectedClip}
            selectedClipType={selectedClipType}
            shot={selectedShot}
            totalDuration={totalDuration}
            videoClipsCount={editorState?.video_clips?.length}
            audioTracksCount={editorState?.audio_tracks?.length}
            sceneGroups={sceneGroups}
            excludedShots={excludedShots}
            onUpdateClipSettings={handleUpdateClipSettings}
            onDeleteClip={handleDeleteClip}
            onChangeTake={handleChangeTake}
            onEditShot={handleEditShot}
            onInitializeFromShots={initializeFromShots}
            onCreateSceneGroup={createSceneGroup}
            onUpdateSceneGroup={updateSceneGroup}
            onDeleteSceneGroup={deleteSceneGroup}
            onAddShotToSceneGroup={addShotToSceneGroup}
            onRemoveShotFromSceneGroup={removeShotFromSceneGroup}
            onReAddShot={handleReAddShot}
            onClearSelection={() => setSelectedClipIds([])}
          />
        </div>
      </div>
      
      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        projectId={projectId}
        projectName={project?.name}
        totalDuration={totalDuration}
        clipCount={editorState?.video_clips?.length || 0}
        getAuthHeaders={getAuthHeaders}
      />
      
      {/* Audio Mixer */}
      {mixerOpen && (
        <AudioMixer
          audioTracks={editorState?.audio_tracks || []}
          masterVolume={masterVolume}
          onTrackVolumeChange={handleTrackVolumeChange}
          onTrackPanChange={handleTrackPanChange}
          onTrackMuteToggle={handleTrackMuteToggle}
          onMasterVolumeChange={setMasterVolume}
          onClose={() => setMixerOpen(false)}
        />
      )}
      
      {/* Upload Progress Modal */}
      {uploadProgress && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 w-80 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{uploadProgress.filename}</p>
                <p className="text-xs text-zinc-400 capitalize">
                  {uploadProgress.stage === 'uploading' && 'Uploading to server...'}
                  {uploadProgress.stage === 'processing' && 'Processing file...'}
                  {uploadProgress.stage === 'encoding' && 'Generating waveform...'}
                </p>
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300"
                style={{ width: `${uploadProgress.progress}%` }}
              />
            </div>
            <p className="text-xs text-zinc-500 text-center mt-2">{uploadProgress.progress}%</p>
          </div>
        </div>
      )}
    </div>
  );
}
